// Opening move: StressCoordinatorService - Thin proxy over shared coordinator
// Epic 03 Pivot: NO heuristics, just delegates to StressAnalysisCoordinator
// Epic 05 Scope 02: Added contextual/batch endpoints, analyzer health checks
// Big play: DI-friendly for testing with configurable baseUrl and fetchImpl

import type { StressPatternResult } from '../types/prosody'
import { StressAnalysisCoordinator } from '../../../services/stressAnalysisCoordinator'

// ============================================================================
// Type Definitions for New Endpoints
// ============================================================================

/**
 * Word analysis from backend /api/stress/analyze endpoint
 *
 * Main play: Represents detailed word-level stress analysis with POS tagging
 */
export interface WordAnalysis {
  word: string
  pos: string // Part-of-speech tag
  syllables: string[]
  stress_pattern: number[] // 0=unstressed, 1=primary, 2=secondary
  reasoning: string // Analysis method used (e.g., "dictionary", "contextual_noun")
  char_positions: number[] // Character indices for stress marks
  confidence: number // 0.0-1.0
}

/**
 * Passage analysis response from backend
 *
 * Big play: Contains aggregated stress data and per-word breakdowns
 * Scope 09: Added expected_stressed_syllables for match quality comparison
 */
export interface PassageAnalysis {
  text: string
  total_syllables: number
  stressed_syllables: number
  processing_time_ms: number
  words: WordAnalysis[]
  expected_stressed_syllables?: number // Scope 09: Expected count for quality comparison
}

/**
 * Batch analysis response for multiple lines
 *
 * Time to tackle the tricky bit: Maps line indices to analysis results
 */
export interface BatchAnalysisResult {
  lines: PassageAnalysis[]
  total_processing_time_ms: number
}

/**
 * Analyzer component health status
 *
 * Victory lap: Reports backend readiness for contextual/POS analysis
 */
export interface AnalyzerStatus {
  healthy: boolean
  spacy_model_loaded: boolean
  cmu_dict_loaded: boolean
  error?: string
}

/**
 * Contextual monosyllable stress result
 *
 * Main play: POS-based stress determination for single-syllable words
 */
export interface MonosyllableContextResult {
  word: string
  stressed: boolean
  pos: string
  confidence: number
  reasoning: string
}

/**
 * StressCoordinatorService interface
 *
 * Main play: Abstract contract for stress analysis operations
 * Time to tackle the tricky bit: Keeps plugins testable by allowing mock implementations
 * Victory lap: Plugins depend on interface, not concrete backend calls
 */
export interface StressCoordinatorService {
  /**
   * Analyze a word to detect its stress pattern (dictionary lookup)
   *
   * @deprecated Use analyzePassage() instead for contextual POS-aware stress analysis.
   * This method only performs dictionary lookups without grammatical context.
   * It should only be used for display/tooltip purposes, not for auto-load workflows.
   *
   * @param word - The word to analyze (will be normalized internally)
   * @param context - Optional surrounding context for disambiguation
   * @param forceReanalysis - Skip cache and force fresh analysis
   * @returns Promise resolving to stress pattern result
   */
  analyzeWord(
    word: string,
    context?: string,
    forceReanalysis?: boolean
  ): Promise<StressPatternResult>

  /**
   * Analyze monosyllable word using contextual POS tagging
   *
   * Epic 05: Contextual override for single-syllable words missing from dictionary
   * Main play: Uses backend spaCy POS analysis to determine stress
   *
   * @param word - The monosyllable word to analyze
   * @param context - Surrounding text for POS disambiguation
   * @returns Promise resolving to contextual stress result
   */
  analyzeMonosyllableContext(
    word: string,
    context: string
  ): Promise<MonosyllableContextResult>

  /**
   * Analyze a complete passage (sentence/line) for stress patterns
   *
   * Epic 05: Full passage analysis with word-level breakdowns
   * Big play: Returns aggregated metrics and per-word stress data
   *
   * @param text - The text passage to analyze
   * @param context - Optional context hint (e.g., 'lyrical')
   * @returns Promise resolving to passage analysis
   */
  analyzePassage(text: string, context?: string): Promise<PassageAnalysis>

  /**
   * Analyze multiple lines in batch for efficiency
   *
   * Epic 05: Batch endpoint for line-by-line analysis
   * Time to tackle the tricky bit: Reduces network overhead for multi-line content
   *
   * @param lines - Array of text lines to analyze
   * @param context - Optional context hint (e.g., 'lyrical')
   * @returns Promise resolving to batch results
   */
  analyzeBatch(lines: string[], context?: string): Promise<BatchAnalysisResult>

  /**
   * Get backend analyzer component health status
   *
   * Epic 05: Health check for gating stress features in UI
   * Victory lap: Enables graceful degradation when backend unavailable
   *
   * @returns Promise resolving to health status
   */
  getAnalyzerStatus(): Promise<AnalyzerStatus>
}

/**
 * Factory configuration for creating StressCoordinatorService
 */
export interface StressCoordinatorServiceConfig {
  baseUrl?: string
  fetchImpl?: typeof fetch
}

/**
 * Default StressCoordinatorService implementation
 *
 * Epic 03: Thin proxy over shared StressAnalysisCoordinator
 * Epic 05 Scope 02: Added contextual/batch methods with fetch binding, caching, error logging
 * Big play: No heuristics - all logic lives in coordinator or backend
 * Main play: Configurable for testing via factory config
 */
export class DefaultStressCoordinatorService implements StressCoordinatorService {
  private coordinator: StressAnalysisCoordinator
  private baseUrl: string
  private fetchImpl: typeof fetch
  private passageCache: Map<string, { result: PassageAnalysis; timestamp: number }> = new Map()
  private readonly CACHE_TTL_MS = 60000 // 1 minute for passage cache

  constructor(config: StressCoordinatorServiceConfig = {}) {
    // Opening move: Create coordinator with injected dependencies
    // Production uses relative URLs (nginx proxies /api/ to backend)
    const { baseUrl = '', fetchImpl = fetch } = config
    this.coordinator = new StressAnalysisCoordinator(baseUrl, fetchImpl)
    this.baseUrl = baseUrl
    // CRITICAL: Bind fetch to globalThis to prevent "Illegal invocation" errors
    // Reference: Epic 05 requirement - fetch must be bound for Playwright tests
    this.fetchImpl = fetchImpl.bind(globalThis)
  }

  async analyzeWord(
    word: string,
    context?: string,
    forceReanalysis = false
  ): Promise<StressPatternResult> {
    // DEPRECATION WARNING: Route auto-load workflows through analyzePassage() instead
    // Reference: Epic lexical_stressing_logic_scope_01_contextual_passage
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[DEPRECATED] analyzeWord("${word}") - Use analyzePassage() for contextual POS-aware stress analysis. ` +
        `This method only performs dictionary lookups without grammatical context.`
      )
    }

    // Victory lap: Just delegate to coordinator, no local logic
    return this.coordinator.analyzeWord({ word, context, forceReanalysis })
  }

  async analyzeMonosyllableContext(
    word: string,
    context: string
  ): Promise<MonosyllableContextResult> {
    // Opening move: Validate inputs
    const cleanWord = word.toLowerCase().replace(/[^a-z']/g, '')
    if (!cleanWord || !context) {
      const errorMsg = `[LEXICAL_STRESS_ERROR] Invalid monosyllable context request: word="${cleanWord}", context="${context}"`
      console.error(errorMsg)
      this.reportError('monosyllable-context-invalid', cleanWord, 'Missing word or context')
      throw new Error('Invalid word or context for monosyllable analysis')
    }

    try {
      // Main play: Call backend contextual endpoint
      const response = await this.fetchImpl(
        `${this.baseUrl}/api/dictionary/contextual-stress`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ word: cleanWord, context }),
        }
      )

      if (!response.ok) {
        const errorMsg = `[LEXICAL_STRESS_ERROR] Contextual stress analysis failed: ${response.status} ${response.statusText}`
        console.error(errorMsg)
        this.reportError('monosyllable-context-http', cleanWord, `HTTP ${response.status}`)
        throw new Error(`Backend returned ${response.status}`)
      }

      const data = await response.json()

      // Big play: Return typed result
      return {
        word: cleanWord,
        stressed: data.stressed ?? false,
        pos: data.pos ?? 'UNKNOWN',
        confidence: data.confidence ?? 0.5,
        reasoning: data.reasoning ?? 'contextual',
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error'
      const errorMsg = `[LEXICAL_STRESS_ERROR] Monosyllable context exception for "${cleanWord}": ${reason}`
      console.error(errorMsg)
      this.reportError('monosyllable-context-exception', cleanWord, reason)
      throw error
    }
  }

  async analyzePassage(text: string, context = 'lyrical'): Promise<PassageAnalysis> {
    // Opening move: Check cache first
    const cacheKey = `${text}:${context}`
    const cached = this.passageCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.result
    }

    try {
      // Main play: Call backend passage analysis endpoint
      const response = await this.fetchImpl(
        `${this.baseUrl}/api/stress/analyze`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, context }),
        }
      )

      if (!response.ok) {
        const errorMsg = `[LEXICAL_STRESS_ERROR] Passage analysis failed: ${response.status} ${response.statusText}`
        console.error(errorMsg)
        this.reportError('passage-analysis-http', text.slice(0, 50), `HTTP ${response.status}`)
        throw new Error(`Backend returned ${response.status}`)
      }

      const data = await response.json()

      // Time to tackle the tricky bit: Type-safe result extraction
      const result: PassageAnalysis = {
        text: data.text ?? text,
        total_syllables: data.total_syllables ?? 0,
        stressed_syllables: data.stressed_syllables ?? 0,
        processing_time_ms: data.processing_time_ms ?? 0,
        words: data.words ?? [],
      }

      // Victory lap: Cache successful result
      this.passageCache.set(cacheKey, { result, timestamp: Date.now() })

      return result
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error'
      const errorMsg = `[LEXICAL_STRESS_ERROR] Passage analysis exception: ${reason}`
      console.error(errorMsg)
      this.reportError('passage-analysis-exception', text.slice(0, 50), reason)
      throw error
    }
  }

  async analyzeBatch(lines: string[], context = 'lyrical'): Promise<BatchAnalysisResult> {
    try {
      // Main play: Call backend batch endpoint
      const response = await this.fetchImpl(
        `${this.baseUrl}/api/stress/analyze-batch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lines, context }),
        }
      )

      if (!response.ok) {
        const errorMsg = `[LEXICAL_STRESS_ERROR] Batch analysis failed: ${response.status} ${response.statusText}`
        console.error(errorMsg)
        this.reportError('batch-analysis-http', `${lines.length} lines`, `HTTP ${response.status}`)
        throw new Error(`Backend returned ${response.status}`)
      }

      const data = await response.json()

      // Big play: Return typed batch result
      return {
        lines: data.lines ?? [],
        total_processing_time_ms: data.total_processing_time_ms ?? 0,
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error'
      const errorMsg = `[LEXICAL_STRESS_ERROR] Batch analysis exception: ${reason}`
      console.error(errorMsg)
      this.reportError('batch-analysis-exception', `${lines.length} lines`, reason)
      throw error
    }
  }

  async getAnalyzerStatus(): Promise<AnalyzerStatus> {
    try {
      // Opening move: Call backend status endpoint
      const response = await this.fetchImpl(
        `${this.baseUrl}/api/stress/analyzer-status`
      )

      if (!response.ok) {
        // Time to tackle the tricky bit: Backend down or unhealthy - return degraded status
        return {
          healthy: false,
          spacy_model_loaded: false,
          cmu_dict_loaded: false,
          error: `Backend returned ${response.status}`,
        }
      }

      const data = await response.json()

      // Big play: Map backend response fields to frontend types
      // Backend returns: { status: "ready"|"error", components: {...}, analyzer_loaded, message }
      const isHealthy = data.status === 'ready' && data.analyzer_loaded === true
      const components = data.components || {}

      // Victory lap: Return typed health status
      return {
        healthy: isHealthy,
        spacy_model_loaded: components.spacy_model ?? false,
        cmu_dict_loaded: components.cmu_dictionary ?? false,
        error: isHealthy ? undefined : (data.message || data.error),
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error'
      const errorMsg = `[LEXICAL_STRESS_ERROR] Analyzer status check failed: ${reason}`
      console.error(errorMsg)
      this.reportError('analyzer-status-exception', 'health-check', reason)

      // Big play: Return graceful degradation instead of throwing
      return {
        healthy: false,
        spacy_model_loaded: false,
        cmu_dict_loaded: false,
        error: reason,
      }
    }
  }

  /**
   * Helper: Report error to instrumentation system
   *
   * Main play: Consistent error logging with window.__LEXICAL_DEBUG__ mirroring
   * Reference: Epic 05 requirement - all failures logged with [LEXICAL_STRESS_ERROR]
   */
  private reportError(type: string, contextStr: string, reason: string): void {
    // Instrumentation hook for debugging (guards against SSR/Jest)
    if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
      window.__LEXICAL_DEBUG__.record?.({
        type: `stress-coordinator-${type}`,
        word: contextStr, // Use 'word' field instead of 'context' for consistency
        reason,
        timestamp: Date.now(),
      })
    }
  }
}

/**
 * Factory function to create default service instance
 *
 * Epic 03: Accepts config for DI-friendly testing
 * Victory lap: Provides easy instantiation for plugins
 */
export function createDefaultStressCoordinatorService(
  config?: StressCoordinatorServiceConfig
): StressCoordinatorService {
  return new DefaultStressCoordinatorService(config)
}

/**
 * Test helper: Create service with custom fetch implementation
 *
 * Main play: Enables isolated testing without real network calls
 * Big play: Tests can inject mock fetch to simulate backend responses
 */
export function createTestStressCoordinatorService(
  mockFetch: typeof fetch,
  baseUrl?: string
): StressCoordinatorService {
  return new DefaultStressCoordinatorService({ baseUrl, fetchImpl: mockFetch })
}

// Victory lap: Service interface defined with minimal proxy implementation
// Plugins depend on interface, tests inject mocks, production uses default
